import React, { Fragment, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { withProps, withHandlers, compose } from 'recompose'
import { toJS } from 'mobx'
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react'
import Dropdown from '../../../Shared/Dropdown'
import PriceInput from '../../../Shared/Form/PriceInput'
import AddressInput from '../AddressInput'
import ListingTypeSelector from '../../Shared/ListingTypeSelector'
import AdvancedFilter from '../AdvancedFilter'
import { priceLabelWithCutOff } from '../../../../lib/utils/money'
import SearchBarFooterActions from '../../Shared/SearchBarFooterActions'
import { enhanceWithMap, AreaMapSelector } from '../../../Shared/Form/TownSelect'
import SelectPricePeriod from '../../Shared/SelectPricePeriod/SelectPricePeriod'
import BedroomsBaths from '../BedroomsBaths'
import Modal from '../../../Shared/Modal'
import { getId, getName } from '../../../../lib/utils/selectors'
import { isDefined, noop } from '../../../../lib/utils/common'

const enhance = compose(
  inject('store'),
  withProps(({ store }) => ({
    uiStateStore: store.uiStateStore,
    searchStateStore: store.searchStateStore,
    listingTypeStore: store.listingTypeStore,
    valueStateStore: store.valueStateStore
  })),
  enhanceWithMap,
  withHandlers(() => {
    const dropdownElements = {}

    const createCancelHandler = (name) => () => () => {
      dropdownElements[name].stateUpdaters.toggleActive()
    }

    return {
      registerDropdown: () => (name, el) => {
        if (el) {
          dropdownElements[name] = el
        }
      },
      cancelPriceDropdown: createCancelHandler('price'),
      cancelBedsBathsDropdown: createCancelHandler('bedsBaths'),
      cancelPricePeriodDropdown: createCancelHandler('pricePeriod'),
      cancelAdvancedFilterDropdown: createCancelHandler('advancedFilter'),
      onChangePricePeriod: ({ valueStateStore }) => value => {
        const anyPeriodId = 'average_monthly_price'
        const currentPeriods = valueStateStore.values.pricePeriods || []
        const specificPeriods = value.filter(id => id !== anyPeriodId)
        const currentSpecificPeriods = currentPeriods.filter(id => id !== anyPeriodId)
        const hasAnyPeriod = value.includes(anyPeriodId)

        if (hasAnyPeriod && specificPeriods.length === 0) {
          valueStateStore.setInputValue('pricePeriods', [])
          valueStateStore.getSearchInfoDebounced()
          return
        }

        if (hasAnyPeriod && specificPeriods.length > 0 && currentSpecificPeriods.length === 0) {
          valueStateStore.setInputValue('pricePeriods', specificPeriods)
          valueStateStore.getSearchInfoDebounced()
          return
        }

        if (hasAnyPeriod && specificPeriods.length > 0 && currentSpecificPeriods.length > 0) {
          valueStateStore.setInputValue('pricePeriods', [])
          valueStateStore.getSearchInfoDebounced()
          return
        }

        valueStateStore.setInputValue('pricePeriods', specificPeriods)
        valueStateStore.getSearchInfoDebounced()
      },
      onChangeYear: ({ valueStateStore }) => value => {
        valueStateStore.setInputValue('year', value)
        valueStateStore.setInputValue('pricePeriods', [])
        valueStateStore.getSearchInfoDebounced()
      },
      onChangePrice: ({ valueStateStore }) => (name, value) => {
        valueStateStore.setInputValue(name, value)
        valueStateStore.getSearchInfoDebounced()
      },
      onChangeTown: ({ valueStateStore }) => ({ value }) => {
        valueStateStore.setInputValue('townNames', value)
        valueStateStore.getSearchInfoDebounced()
      },
      onRadioButtonChange: ({ valueStateStore }) => name => value => {
        valueStateStore.setInputValue(name, value)
        valueStateStore.getSearchInfoDebounced()
      },
      onImmediateSubmit: ({ valueStateStore }) => () => {
        valueStateStore.fetchDataForListings()
        valueStateStore.getSearchInfoDebounced()
      }
    }
  }),
  observer,
)

const SearchBarDesktop = ({
  valueStateStore,
  listingTypeStore,
  uiStateStore,
  searchStateStore,
  registerDropdown,
  cancelPriceDropdown,
  cancelBedsBathsDropdown,
  cancelPricePeriodDropdown,
  cancelAdvancedFilterDropdown,
  onChangePricePeriod,
  onChangePrice,
  onChangeTown,
  onImmediateSubmit,
  onChangeYear,
  onRadioButtonChange,
  openMapView,
  isMapOpen,
  setMapOpen
}) => {
  const [isResetFilters, setResetFilters] = useState(false)
  const [isListingTypeActive, setIsListingTypeActive] = useState(false)
  const [isPriceActive, setIsPriceActive] = useState(false)
  const [isBedsBathsActive, setIsBedsBathsActive] = useState(false)
  const [isPricePeriodActive, setIsPricePeriodActive] = useState(false)
  const [isAdvancedFilterActive, setIsAdvancedFilterActive] = useState(false)

  const removePolygonIfFiltersWereReset = useCallback(() => {
    if (isResetFilters) {
      valueStateStore.removePolygon()
    }
  }, [isResetFilters, valueStateStore])

  const handleSubmitTownChanges = useCallback(() => {
    valueStateStore.clearMapBounds()
    valueStateStore.removePolygon()
    onImmediateSubmit()
  }, [onImmediateSubmit, valueStateStore])

  const handleAdvancedFilterChange = useCallback(() => {
    if ((valueStateStore.values.townNames || []).length > 0 || isResetFilters) {
      valueStateStore.removePolygon()
    }

    valueStateStore.clearMapBounds()
    onImmediateSubmit()
  }, [onImmediateSubmit, isResetFilters, valueStateStore])

  const optionTemplate = useCallback(({ labelText, option }) => (
    <span>
      {labelText}
      {isDefined(option.count) && <span className='Multiselect-locationCount'>{` (${option.count})`}</span>}
    </span>
  ), [])

  const handleMapSelect = useCallback((option) => {
    const currentValues = valueStateStore.values.townNames || []
    const optionId = getId(option)
    const newValues = currentValues.includes(optionId)
      ? currentValues.filter(id => id !== optionId)
      : [...currentValues, optionId]

    onChangeTown({ value: newValues })
  }, [valueStateStore.values.townNames, onChangeTown])

  return (
    <Fragment>
      <div className='SearchBar'>
        <div className='SearchBar-input SearchBar-input--address'>
        <AddressInput
          value={valueStateStore.values.address || ''}
          onChange={(e) => valueStateStore.setInputValue('address', e.target.value)}
          placeholder='Address, neighbourhood, city, ZIP'
          options={toJS(valueStateStore.treeTownOptions)}
          optionsById={valueStateStore.optionsById}
          selectedOptionsWithParents={valueStateStore.selectedOptionsWithParents}
          selectedOptionsGroup={toJS(valueStateStore.selectedOptionsGroup)}
          onApplyResults={handleSubmitTownChanges}
          countResults={valueStateStore.countResults}
          onChangeTown={onChangeTown}
          enableAutocomplete={true}
          openMapView={openMapView}
        />
        </div>

        <div className={`SearchBar-input SearchBar-input--listingType ${isListingTypeActive ? 'is-active' : ''}`}>
        <ListingTypeSelector
          currentListingType={listingTypeStore.current}
          listingTypes={listingTypeStore.listingTypes}
          onChange={
            lt => {
              valueStateStore.saveSearchScope(lt)
              listingTypeStore.setCurrent(lt)
              valueStateStore.removePolygon()
              onImmediateSubmit()
            }
          }
          onActiveChange={setIsListingTypeActive}
          DropdownComponent={Dropdown}
          showLabel={false}
        />
        </div>

        {listingTypeStore.current === 'rentals' &&
        <div className={`SearchBar-input ${isPricePeriodActive ? 'is-active' : ''}`}>
        <SelectPricePeriod
          id='price-period'
          pricePeriodYears={uiStateStore.pricePeriodYears}
          selectedYear={valueStateStore.values.year}
          pricePeriods={uiStateStore.pricePeriods}
          selectedPricePeriod={valueStateStore.values.pricePeriods || []}
          onChangePricePeriod={onChangePricePeriod}
          onChangeYear={onChangeYear}
          onResetInputToInitialValue={valueStateStore.resetInputToInitialValue}
          onActiveChanged={setIsPricePeriodActive}
          registerDropdown={registerDropdown}
          DropdownComponent={Dropdown}
          footer={
            <SearchBarFooterActions
              countResults={valueStateStore.countResults}
              cancelHandler={cancelPricePeriodDropdown}
              clearHandler={() => {
                valueStateStore.resetInputs(['pricePeriods', 'year'])
                valueStateStore.getSearchInfoDebounced()
              }}
              applyResultsHandler={() => {
                removePolygonIfFiltersWereReset()
                onImmediateSubmit()
                cancelPricePeriodDropdown()
              }}
            />
          }
        />
        </div>
        }

        <div className={`SearchBar-input ${isPriceActive ? 'is-active' : ''}`}>
        <Dropdown
          id='price'
          className='Dropdown--searchBar'
          ref={registerDropdown.bind(null, 'price')}
          onActiveChanged={setIsPriceActive}
          title={
            <Fragment>
              <div>{valueStateStore.priceTitle(uiStateStore.priceExtremum.cutOff)}</div>
            </Fragment>
          }
        >
          <div className='Dropdown-container'>
            <PriceInput
              priceExtremum={uiStateStore.priceExtremum}
              values={valueStateStore.priceValues}
              onChange={onChangePrice}
              onReset={valueStateStore.resetInputs}
              handleTemplate={priceLabelWithCutOff}
            />
          </div>
          <SearchBarFooterActions
            cancelHandler={cancelPriceDropdown}
            clearHandler={() => {
              valueStateStore.resetInputs(['priceFrom', 'priceTo'])
              valueStateStore.getSearchInfoDebounced()
            }}
            applyResultsHandler={() => {
              removePolygonIfFiltersWereReset()
              onImmediateSubmit()
              cancelPriceDropdown()
            }}
            countResults={valueStateStore.countResults}
          />
        </Dropdown>
        </div>

        {(listingTypeStore.current === 'rentals' || listingTypeStore.current === 'sales') &&
        !valueStateStore.values.unitTypes?.includes('land') &&
        <div className={`SearchBar-input SearchBar-input--bedsBaths ${isBedsBathsActive ? 'is-active' : ''}`}>
        <Dropdown
          id='bedrooms'
          name='bedrooms'
          className='Dropdown--searchBar'
          title={
            <Fragment>
              <div className='sr-only'>Bedrooms and Bathrooms</div>
              <div>{valueStateStore.bedsBathsTitle}</div>
            </Fragment>
          }
          ref={registerDropdown.bind(null, 'bedsBaths')}
          onActiveChanged={setIsBedsBathsActive}
        >
          <div className='Dropdown-container Dropdown-container--stacked'>
            <div className='Dropdown-itemColumn'>
              <BedroomsBaths
                name='beds'
                options={uiStateStore.bedsBathsOptions}
                onChange={onRadioButtonChange('beds')}
                value={valueStateStore.values.beds}
                legend='Bedrooms'
              />
            </div>
            <div className='Dropdown-itemColumn'>
              <BedroomsBaths
                name='baths'
                options={uiStateStore.bedsBathsOptions}
                onChange={onRadioButtonChange('baths')}
                value={valueStateStore.values.baths}
                legend='Bathrooms'
              />
            </div>
          </div>
          <SearchBarFooterActions
            cancelHandler={cancelBedsBathsDropdown}
            clearHandler={() => {
              valueStateStore.resetInputs(['beds', 'baths'])
              valueStateStore.getSearchInfoDebounced()
            }}
            applyResultsHandler={() => {
              removePolygonIfFiltersWereReset()
              onImmediateSubmit()
              cancelBedsBathsDropdown()
            }}
            countResults={valueStateStore.countResults}
          />
        </Dropdown>
        </div>
        }

        <div className={`SearchBar-input SearchBar-input--action ${isAdvancedFilterActive ? 'is-active' : ''}`}>
        <AdvancedFilter
          submit={handleAdvancedFilterChange}
          reset={() => {
            const didReset = valueStateStore.handleResetFilters()
            setResetFilters(didReset)
          }}
          onActiveChanged={setIsAdvancedFilterActive}
          registerDropdown={registerDropdown}
          cancelDropdown={cancelAdvancedFilterDropdown}
        />
        </div>

        <div className='SearchBar-input SearchBar-input--last SearchBar-input--action'>
        {searchStateStore.search.saved ?
          <button
            type='button'
            className='Button Button--secondary Button--height40 Button--searchBell Button--iconPrefix'
            onClick={() => { searchStateStore.destroySearch() }}
          >
            Saved
          </button>
          :
          <button
            type='button'
            className='Button Button--secondary Button--height40 Button--searchBell Button--iconPrefix'
            onClick={() => {
              searchStateStore.saveSearch({ values: valueStateStore.valuesForSubmit })
            }}
          >
            Save search
          </button>
        }
        </div>
      </div>

      <Modal
      isOpen={isMapOpen}
      onRequestClose={() => setMapOpen(false)}
      ariaHideApp={false}
      contentLabel='Area map'
      portalClassName='ReactModalPortal exclude-mobile-viewport'
      className='Modal-content Modal-content--tablet-fullWidth'
    >
      <AreaMapSelector
        valueStateStore={valueStateStore}
        rawOptions={toJS(valueStateStore.treeTownOptions)}
        options={toJS(valueStateStore.treeTownOptions)}
        selectedOptions={toJS(valueStateStore.selectedOptionsGroup)}
        optionsById={valueStateStore.optionsById}
        optionIdentity={getId}
        optionTemplate={optionTemplate}
        optionLabel={getName}
        onSelect={handleMapSelect}
        onMouseEnter={noop}
        onMouseLeave={noop}
        areaMapSelectorActions={() => (
          <div className='AreaMapSelector-actions'>
            <div className='AreaMapSelector-action'>
              <button type='button' className='Button Button--secondary Button--width250'
                onClick={(e) => {
                  e.stopPropagation()
                  setMapOpen(false)
                }}
              >
                Cancel
              </button>
            </div>
            <div className='AreaMapSelector-action'>
              <button type='button' className='Button Button--primary Button--width250'
                onClick={() => {
                  onImmediateSubmit()
                  setMapOpen(false)
                }}>
                See results ({valueStateStore.countResults})
              </button>
            </div>
          </div>
        )}
      />
      </Modal>
    </Fragment>
  )
}

SearchBarDesktop.propTypes = {
  cancelAdvancedFilterDropdown: PropTypes.func.isRequired,
  cancelBedsBathsDropdown: PropTypes.func.isRequired,
  cancelPriceDropdown: PropTypes.func.isRequired,
  cancelPricePeriodDropdown: PropTypes.func.isRequired,
  isMapOpen: PropTypes.bool.isRequired,
  listingTypeStore: PropTypes.shape({
    listingTypes: PropTypes.object.isRequired,
    current: PropTypes.string.isRequired,
    setCurrent: PropTypes.func.isRequired
  }).isRequired,
  onChangePrice: PropTypes.func.isRequired,
  onChangePricePeriod: PropTypes.func.isRequired,
  onChangeTown: PropTypes.func.isRequired,
  onChangeYear: PropTypes.func.isRequired,
  onImmediateSubmit: PropTypes.func.isRequired,
  onRadioButtonChange: PropTypes.func,
  openMapView: PropTypes.func.isRequired,
  registerDropdown: PropTypes.func.isRequired,
  searchStateStore: PropTypes.shape({
    search: PropTypes.object.isRequired,
    destroySearch: PropTypes.func.isRequired,
    saveSearch: PropTypes.func.isRequired
  }),
  setMapOpen: PropTypes.func.isRequired,
  uiStateStore: PropTypes.shape({
    priceExtremum: PropTypes.object.isRequired,
    pricePeriods: MobxPropTypes.objectOrObservableObject.isRequired,
    pricePeriodYears: PropTypes.array.isRequired,
    bedsBathsOptions: PropTypes.array
  }).isRequired,
  valueStateStore: PropTypes.shape({
    values: PropTypes.object.isRequired,
    selectedOptionsWithParents: PropTypes.array,
    selectedOptionsGroup: MobxPropTypes.arrayOrObservableArray,
    optionsById: PropTypes.object.isRequired,
    towns: PropTypes.object.isRequired,
    bedsValueLimits: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired
    }).isRequired,
    bathValueLimits: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired
    }).isRequired,
    priceValues: PropTypes.array.isRequired,
    searchPolygon: PropTypes.array.isRequired,
    setInputValue: PropTypes.func.isRequired,
    debounceSubmit: PropTypes.func.isRequired,
    immediateSubmit: PropTypes.func.isRequired,
    resetAllInputs: PropTypes.func.isRequired,
    countResults: PropTypes.number.isRequired,
    treeTownOptions: PropTypes.array.isRequired,
    priceTitle: PropTypes.func.isRequired,
    resetInputs: PropTypes.func.isRequired,
    saveSearchScope: PropTypes.func.isRequired,
    getSearchInfoDebounced: PropTypes.func.isRequired,
    bedsBathsTitle: PropTypes.string.isRequired,
    valuesForSubmit: PropTypes.object.isRequired,
    mapBounds: PropTypes.object.isRequired,
    resetInputToInitialValue: PropTypes.func.isRequired,
    year: PropTypes.string,
    pricePeriod: PropTypes.string
  }).isRequired
}

export default enhance(SearchBarDesktop)
